import type { ExtensionInstallationSummary } from "@api-client";
import { formatMoney } from "@sylviq/ui-core";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@sylviq/ui-react-web/components/ui/card";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { useState } from "react";
import { ExtensionDocumentation, extensionDisplayMetadata } from "./ExtensionDocumentation";
import { latestCatalogVersion } from "./extension-updates";
import { type ExtensionCatalogPackage, resolveExtensionDocsUrl } from "@/lib/api";
import {
  DirectBuildInstallation,
  capabilitySummary,
  installationCapabilities,
  installationTargetApps,
} from "@/features/build/DirectBuildInstallation";

type CatalogListState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; items: ExtensionCatalogPackage[] };

type InstallationListState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; items: ExtensionInstallationSummary[] };

function catalogPricingLabel(catalogPackage: ExtensionCatalogPackage): string {
  if (catalogPackage.pricing === "free") return "Free";
  if (catalogPackage.price_cents !== null && catalogPackage.currency) {
    return formatMoney({ cents: catalogPackage.price_cents, currency: catalogPackage.currency });
  }
  return catalogPackage.pricing;
}

function catalogDocumentationUrl(catalogPackage: ExtensionCatalogPackage): string | null {
  const docsUrl = (catalogPackage as { docs_url?: unknown }).docs_url;
  return resolveExtensionDocsUrl(typeof docsUrl === "string" ? docsUrl : "");
}

function targetAppsLabel(targetApps: string[] | "all"): string {
  return targetApps === "all"
    ? "All apps (organization context required)"
    : targetApps.join(", ") || "None declared";
}

export function ExtensionsBrowsePanel({
  organizationId,
  userId,
  canInstall,
  catalog,
  installations,
  onInstalled,
}: {
  organizationId: string;
  userId: string;
  canInstall: boolean;
  catalog: CatalogListState;
  installations: InstallationListState;
  onInstalled: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [detailsForId, setDetailsForId] = useState<string | null>(null);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visiblePackages = catalog.status !== "loaded"
    ? []
    : normalizedQuery
      ? catalog.items.filter((catalogPackage) =>
          `${catalogPackage.publisher}/${catalogPackage.key} ${extensionDisplayMetadata(latestCatalogVersion(catalogPackage)?.manifest).summary ?? ""}`.toLowerCase().includes(normalizedQuery),
        )
      : catalog.items;

  const detailsPackage = detailsForId !== null && catalog.status === "loaded"
    ? catalog.items.find((catalogPackage) => catalogPackage.id === detailsForId) ?? null
    : null;
  const detailsVersion = detailsPackage ? latestCatalogVersion(detailsPackage) : null;
  const detailsPermissions = capabilitySummary(installationCapabilities(detailsVersion?.manifest));
  const detailsTargetApps = detailsVersion ? installationTargetApps(detailsVersion.manifest) : [];
  const detailsDocumentationUrl = detailsPackage ? catalogDocumentationUrl(detailsPackage) : null;

  return (
    <section aria-labelledby="available-extensions-heading" className="space-y-4">
      <h2 id="available-extensions-heading" className="text-lg font-semibold">
        Available extensions
      </h2>
      <p className="text-sm text-[var(--muted-foreground)]">
        Published releases offered to this organization's organization. Install applies the selected release and any missing required dependencies to this organization.
      </p>
      {catalog.status === "loading" ? (
        <p role="status" className="text-sm text-[var(--muted-foreground)]">
          Loading available extensions...
        </p>
      ) : null}
      {catalog.status === "error" ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {catalog.message}
        </p>
      ) : null}
      {catalog.status === "loaded" && catalog.items.length === 0 ? (
        <EmptyState
          title="No extensions offered"
          reason="No published extensions are available to this organization's organization yet."
        />
      ) : null}
      {catalog.status === "loaded" && catalog.items.length > 0 ? (
        <>
          <label className="grid max-w-md gap-1.5 text-sm font-medium">
            Search extensions
            <Input
              aria-label="Search extensions"
              autoComplete="off"
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Filter by publisher or key"
              type="search"
              value={searchQuery}
            />
          </label>
          {visiblePackages.length === 0 ? (
            <div role="status">
              <EmptyState
                title="No matching extensions"
                reason="No offered extension matches your search. Clear the search to see all published extensions."
              />
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {visiblePackages.map((catalogPackage) => {
                const latest = latestCatalogVersion(catalogPackage);
                const targetApps = latest ? installationTargetApps(latest.manifest) : [];
                const installed = installations.status === "loaded" &&
                  installations.items.some((installation) => installation.package_id === catalogPackage.id);
                return (
                  <Card key={catalogPackage.id}>
                    <CardHeader>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <CardTitle>{catalogPackage.publisher}/{catalogPackage.key}</CardTitle>
                        <Badge tone={installed ? "success" : "neutral"}>
                          {installed ? "installed" : catalogPackage.visibility}
                        </Badge>
                      </div>
                      <CardDescription>
                        {latest ? `Version ${latest.version} · API ${latest.api_version}` : "No published version"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-3">
                      {extensionDisplayMetadata(latest?.manifest).summary ? <p className="text-sm">{extensionDisplayMetadata(latest?.manifest).summary}</p> : null}
                      <p className="text-sm text-[var(--muted-foreground)]">
                        {catalogPricingLabel(catalogPackage)}
                      </p>
                      <Button
                        aria-label={`Details for ${catalogPackage.publisher}/${catalogPackage.key}`}
                        onClick={() => setDetailsForId(catalogPackage.id)}
                        size="sm"
                        variant="outline"
                      >
                        Details
                      </Button>
                      {installed ? (
                        <p className="text-sm text-[var(--muted-foreground)]">
                          This package is already installed in this organization.
                        </p>
                      ) : latest && targetApps !== "all" && targetApps.length === 0 ? (
                        <p className="text-sm text-[var(--muted-foreground)]">
                          This release does not target an app that can be installed here.
                        </p>
                      ) : latest && canInstall ? (
                        <DirectBuildInstallation
                          key={`${organizationId}:${organizationId}:${catalogPackage.id}:${latest.created_at}:${userId}`}
                          organizationId={organizationId}
                          userId={userId}
                          packageId={catalogPackage.id}
                          publisher={catalogPackage.publisher}
                          packageKey={catalogPackage.key}
                          version={latest.version}
                          targetApps={targetApps}
                          permissions={installationCapabilities(latest.manifest)}
                          onInstalled={onInstalled}
                        />
                      ) : latest ? (
                        <p className="text-sm text-[var(--muted-foreground)]">
                          Only the organization owner can install extensions.
                        </p>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      ) : null}
      <Dialog
        description="Release metadata declared by the publisher."
        onOpenChange={(open) => {
          if (!open) setDetailsForId(null);
        }}
        open={detailsPackage !== null}
        title="Extension details"
      >
        {detailsPackage ? (
          <div className="max-h-[min(60dvh,calc(100dvh-16rem))] space-y-4 overflow-y-auto">
            <p className="text-sm font-medium">{detailsPackage.publisher}/{detailsPackage.key}</p>
            <dl className="grid gap-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted-foreground)]">Publisher</dt>
                <dd className="font-medium">{detailsPackage.publisher}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted-foreground)]">Key</dt>
                <dd className="font-medium">{detailsPackage.key}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted-foreground)]">Version</dt>
                <dd className="font-medium">{detailsVersion ? detailsVersion.version : "No published version"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted-foreground)]">API version</dt>
                <dd className="font-medium">{detailsVersion ? detailsVersion.api_version : "Unavailable"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[var(--muted-foreground)]">Pricing</dt>
                <dd className="font-medium">{catalogPricingLabel(detailsPackage)}</dd>
              </div>
            </dl>
            <section aria-label="Supported apps" className="space-y-1 text-sm">
              <h3 className="font-medium">Supported apps</h3>
              <p className="text-[var(--muted-foreground)]">
                {detailsVersion ? targetAppsLabel(detailsTargetApps) : "Unknown"}
              </p>
            </section>
            <ExtensionDocumentation publisher={detailsPackage.publisher} extensionKey={detailsPackage.key} metadata={detailsVersion?.manifest} />
            <section aria-label="Declared permissions" className="space-y-1 text-sm">
              <h3 className="font-medium">Requested permissions</h3>
              {detailsPermissions.valid ? (
                detailsPermissions.labels.length > 0 ? (
                  <ul className="list-inside list-disc text-[var(--muted-foreground)]">
                    {detailsPermissions.labels.map((permission, index) => <li key={`${permission}-${index}`}>{permission}</li>)}
                  </ul>
                ) : (
                  <p className="text-[var(--muted-foreground)]">No organization capabilities requested.</p>
                )
              ) : (
                <p className="text-[var(--danger)]" role="status">Permissions unavailable; this release cannot be installed until its declaration is verified.</p>
              )}
            </section>
            <section aria-label="Documentation" className="space-y-1 text-sm">
              <h3 className="font-medium">Documentation</h3>
              {detailsDocumentationUrl ? (
                <a
                  aria-label="Extension documentation"
                  className="inline-block text-sm underline"
                  href={detailsDocumentationUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  Documentation
                </a>
              ) : (
                <p className="text-[var(--muted-foreground)]">No documentation published.</p>
              )}
            </section>
          </div>
        ) : null}
      </Dialog>
    </section>
  );
}
